import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { GenericFormPage } from '../../components/generic';
import { useAuth } from '../../contexts/AuthContext';
import { useAppointment, Appointment, useAppStatusesForSelect } from './useData';
import {
  moduleConfig,
  getInitialFormData,
  mapItemToFormData,
} from './config';

const Form: React.FC = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const scheduleDateTimeFromUrl = searchParams.get('clientAppScheduleDateTime');
  const { options: appStatusOptions } = useAppStatusesForSelect();

  const configWithOptions = (() => {
    const fields = moduleConfig.fields.map((field) => {
      if (field.name === 'clientId') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Client',
          placeholder: 'Select a client (optional)',
          options: [{ value: '', label: 'None' }],
          apiEndpoint: '/crm-clients',
          labelField: 'clientName',
          valueField: 'clientId' as const,
        };
      }
      if (field.name === 'assignedTo') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Asgn',
          placeholder: 'Select user',
          options: [],
          apiEndpoint: '/users',
          labelField: 'userDisplayName',
          valueField: 'id' as const,
        };
      }
      if (field.name === 'clientAppDuration') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Duration',
          placeholder: 'Select duration (optional)',
          options: [{ value: '', label: 'None' }],
          apiEndpoint: '/p-crm-duration',
          labelField: 'pd_descr',
          valueField: 'id' as const,
        };
      }
      if (field.name === 'clientAppStatus') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Status',
          placeholder: 'Select status',
          options: appStatusOptions,
        };
      }
      return field;
    });

    // Form data allows foreign keys as string for select components
    type FormAppointment = Omit<Appointment, 'clientId' | 'assignedTo' | 'clientAppDuration' | 'clientAppStatus'> & {
      clientId?: string | number;
      assignedTo?: string | number;
      clientAppDuration?: string | number;
      clientAppStatus?: string | number;
    };

    // Custom mapItemToFormData that converts foreign keys to string for select
    const customMapItemToFormData = (item: Appointment): Partial<FormAppointment> => {
      const baseData = mapItemToFormData(item);
      return {
        ...baseData,
        clientId: baseData.clientId ? String(baseData.clientId) : '',
        assignedTo: baseData.assignedTo ? String(baseData.assignedTo) : '',
        clientAppDuration: baseData.clientAppDuration ? String(baseData.clientAppDuration) : '',
        clientAppStatus: baseData.clientAppStatus ? String(baseData.clientAppStatus) : '',
      };
    };

    // Custom getInitialFormData: new appointment defaults status to 1 and assignedTo to logged-in user
    const customGetInitialFormData = (): Partial<FormAppointment> => {
      return {
        ...getInitialFormData(),
        clientId: '',
        assignedTo: user?.id != null ? String(user.id) : '',
        clientAppDuration: '',
        clientAppStatus: '1',
        clientAppScheduleDateTime: scheduleDateTimeFromUrl || undefined,
      };
    };

    // Helper function to convert string to number or undefined
    const convertToNumber = (value: string | number | undefined): number | undefined => {
      if (value === undefined || value === null || String(value) === '') {
        return undefined;
      }
      return Number(value);
    };

    // Custom create/update handlers that convert foreign key strings back to numbers
    const customCreate = async (data: Partial<FormAppointment>) => {
      const processedData: Partial<Appointment> = {
        ...data,
        clientId: convertToNumber(data.clientId),
        assignedTo: convertToNumber(data.assignedTo) as number, // Required field
        clientAppDuration: convertToNumber(data.clientAppDuration),
        clientAppStatus: convertToNumber(data.clientAppStatus) as number, // Required field
      };
      return await moduleConfig.form.create(processedData);
    };

    const customUpdate = async (id: number, data: Partial<FormAppointment>) => {
      const processedData: Partial<Appointment> = {
        ...data,
        clientId: convertToNumber(data.clientId),
        assignedTo: convertToNumber(data.assignedTo) as number, // Required field
        clientAppDuration: convertToNumber(data.clientAppDuration),
        clientAppStatus: convertToNumber(data.clientAppStatus) as number, // Required field
      };
      return await moduleConfig.form.update(id, processedData);
    };

    return {
      ...moduleConfig,
      fields,
      getInitialFormData: customGetInitialFormData as () => Partial<Appointment>,
      mapItemToFormData: customMapItemToFormData as (item: Appointment) => Partial<Appointment>,
      form: {
        ...moduleConfig.form,
        create: customCreate as (data: Partial<Appointment>) => Promise<Appointment>,
        update: customUpdate as (id: number, data: Partial<Appointment>) => Promise<Appointment>,
      },
    };
  })();

  return (
    <GenericFormPage<Appointment>
      useData={useAppointment}
      config={configWithOptions}
    />
  );
};

export default Form;


